import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Head from "../../../../../layout/head/Head";
import Content from "../../../../../layout/content/Content";
import {
  Row,
  Col,
  Icon,
} from "src/components/Component";

import {
  Button,
  CardFooter,
  Card,
  CardBody,
  Spinner,
  CardHeader, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup
} from "reactstrap";
import API_ROUTES, { FRONTEND_ROUTES } from "../../../../../config/api";
import { Link, useHistory, useParams } from "react-router-dom";
import Axios from "axios";
import CurrencyFormat from "../../../../../transformer/currency-format";
import { MOTO_IN_PERSON, MOTO_OTHER } from '../../../../../constants/transaction-methods';
import { Auth } from "aws-amplify";
import { convertPenniesToDecimals } from "../../../../../utils/money";
import { QRCode } from "react-qr-code";
import { TrashIcon, ClipboardIcon, EnvelopeIcon, WindowIcon, PhoneIcon } from "@heroicons/react/24/outline";

import { InformationCircleIcon } from '@heroicons/react/20/solid';
import domain from 'src/config/domain';

const ProjectCardPage = () => {
  const domainConfig = domain();

  const { id } = useParams();

  const { enums } = useSelector((state) => state.enums);
  const { account } = useSelector((state) => state.account);
  const organisation = useSelector((state) => state.organisation);

  const [alert, setAlert] = useState(null);
  const [data, setData] = useState(null);
  const [isDeletable, setIsDeletable] = useState(false);
  const [archiveModelForm, setArchiveModelForm] = useState(false);
  const [archiveModelFormProcessing, setArchiveModelFormProcessing] = useState(false);

  const history = useHistory();

  const url = FRONTEND_ROUTES.pay["payment-link"](id);

  const getPaymentLink = useCallback(async () => {
    const user = await Auth.currentAuthenticatedUser();

    const result = await Axios(
      API_ROUTES.agent['payment-link:get'](id),
      {
        method: 'get',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    setData(result.data.data);

    // Figure out whether the payment link is deletable
    const statuses = result?.data?.data?.transactions ? result?.data?.data?.transactions?.map((transaction) => transaction.status) : [];

    if (
      statuses.length === 0 ||
      (
        !statuses.includes('COMPLETE') &&
        !statuses.includes('PENDING') &&
        !statuses.includes('AUTHORIZED') &&
        !statuses.includes('PAYMENTINPROGRESS')
      )
    ) {
      setIsDeletable(true);
    }
  }, []);

  useEffect(() => {
    getPaymentLink();
  }, []);

  const copyLinkToClipboard = async () => {
    setAlert('The payment link has been copied to your clipboard.')

    if ('clipboard' in navigator) {
      return await navigator.clipboard.writeText(url);
    } else {
      return document.execCommand('copy', true, url);
    }
  }

  const toggleArchiveModelForm = () => setArchiveModelForm(!archiveModelForm);

  /**
   * Open the payment link url
   * @param e
   * @param method
   */
  const openPaymentLink = (e, method = null) => {
    e.preventDefault();

    let linkUrl = url;
    if (method !== null) {
      linkUrl = `${url}?method=${method}`;
    }

    window.open(linkUrl, '_blank');
  }

  /**
   * Archive the payment link
   * @returns {Promise<void>}
   */
  const archivePaymentLink = async () => {
    const user = await Auth.currentAuthenticatedUser();
    setArchiveModelFormProcessing(true);

    await Axios(
      API_ROUTES.agent['payment-link:delete'](id),
      {
        method: 'delete',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    history.push({
      pathname: '/payment-links',
    });
  };

  const emailLink = !data ? '' : `mailto:${data.email}?subject=Booking Payment Request - ${data.organisation.name}&body=Hi ${data.customer_name}, %0D%0A %0D%0A Please make the Payment of ${CurrencyFormat(enums.currency, data.currency).format(convertPenniesToDecimals(data.amount))} to ${data.organisation.name} using the following link ${url} %0D%0A %0D%0A Many Thanks, %0D%0A ${account.first_name}`;

  return (
    <React.Fragment>
      <Head title="Payment Link Details" />
      <Content>
        {alert && (
          <Card className="flex flex-row w-full border border-violet-200 rounded-lg px-4 py-2 bg-violet-50 mb-4">
            <div className="w-100 text-violet-800">
              <div className='float-left mr-2'>
                <InformationCircleIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-violet-900 text-sm">
                  {alert}
                </p>
              </div>
            </div>
          </Card>
        )}

        {!data && (
          <div className="text-center mt-5 fs-21px">
            <Spinner color="primary"> </Spinner>
          </div>
        )}

        {data && (
          <div className="grid grid-cols-1">
            <div className="grid grid-cols-1 lg:grid-cols-4 lg:gap-4">

              <Card className="card-bordered rounded-xl clearfix grid-cols-4 mb-4 lg:mb-0">
                <CardBody className="card-inner" style={{ minHeight: '100px' }}>

                  <div className="row g-3">

                    <div className="col-12 mb-2">
                      <span className="sub-text font-weight-bold text-primary">Payment Link ID:</span>
                      <span>{id}</span>
                    </div>

                    <div className="col-12 mb-2">
                      <span className="sub-text font-weight-bold text-primary">Booking ID:</span>
                      <span>
                        {data?.booking !== null && (
                          <React.Fragment>
                            <Link className={`${domainConfig.styles.textColour} underline mt-0.5 block float-left`} to={`/booking/${data.booking.id}`}>{data.booking.booking_reference}</Link>
                            <Link to={`/payment-links/${id}/re-assign`} className={`${domainConfig.styles.primaryColour} text-white px-2 py-1 rounded-md text-xs ml-2 block float-left hover:bg-slate-700`}> Re-assign</Link>
                          </React.Fragment>
                        )}
                        {data.booking === null && (
                          <React.Fragment>
                            <strong>Unassigned</strong> | <Link to={`/payment-links/${id}/assign`} className={`${domainConfig.styles.textColour} underline`}>Assign to Booking</Link>
                          </React.Fragment>
                        )}
                      </span>
                    </div>

                    <div className="col-12 mb-2">
                      <span className="sub-text font-weight-bold text-primary">Amount:</span>
                      <span>{CurrencyFormat(enums.currency, data.currency).format(convertPenniesToDecimals(data.amount))}</span>
                    </div>

                    <div className="col-12 mb-2">
                      <span className="sub-text font-weight-bold text-primary">Customer:</span>
                      <span>{data?.customer?.id
                        ? <Link to={`/customer/${data.customer.id}`} className={`${domainConfig.styles.textColour} underline`}>{data.customer_name}</Link>
                        : data.customer_name
                      }</span>
                    </div>

                    {
                      data.description && (
                        <div className="col-12">
                          <span className="sub-text font-weight-bold text-primary">Description:</span>
                          <span>{data.description}</span>
                        </div>
                      )
                    }
                  </div>

                  {isDeletable === true && (
                    <div className="mt-10 w-100">
                      <Button color="danger" className="ml-0 border text-red-500 border-red-500 w-100 text-center justify-center" onClick={toggleArchiveModelForm}>
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Archive Payment Link
                      </Button>
                    </div>
                  )}

                </CardBody>
              </Card>

              <div className="col-span-3">

                <Row className="g-gs">

                  <Col lg="12">
                    <Card className="card-bordered float-left w-100 col-span-3 rounded-lg block">

                      <div className="float-right w-[200px] block">
                        <QRCode
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "200px" }}
                          value={url}
                          viewBox={`0 0 256 256`}
                        />
                      </div>

                      <div className="block float-left" style={{ width: 'calc(100% - 200px)' }}>

                        <CardHeader className="bg-slate-300 text-slate-800 py-2 px-4 relative">
                          <h5 className="title font-extrabold">Send Payment Link</h5>
                        </CardHeader>

                        <CardBody className="card-inner col-span-3" style={{ minHeight: '100px' }}>
                          <p>To send the payment link to your customer either...<br />Click on <span className="italic font-bold">'Email Customer'</span>, click <span className="italic font-bold">'Copy Payment Link URL'</span> button or have your customer <span className="italic font-bold">scan the QR code</span>.</p>
                        </CardBody>

                        <CardFooter className="clearfix">
                          <Button className="bg-violet-800 text-white ml-2 float-right border-0" onClick={() => { window.location.href = emailLink }}>
                            <EnvelopeIcon className="h-4 w-4 mr-1" />
                            Email Customer
                          </Button>

                          <Button className="bg-violet-800 text-white float-right border-0" onClick={copyLinkToClipboard}>
                            <ClipboardIcon className="h-4 w-4 mr-1" />
                            Copy Payment Link URL
                          </Button>
                        </CardFooter>
                      </div>


                    </Card>
                  </Col>

                  <Col md="6">
                    <Card className="card-bordered rounded-lg">
                      <CardHeader className="bg-slate-300 text-slate-800 py-2 px-4relative">
                        <div className=" absolute top-0 right-0 text-violet-100 bg-slate-500 text-sm float-right -mt-[1px] px-4 py-[11.2px] rounded-xs font-bold">
                          MOTO
                        </div>
                        <h5 className="title font-extrabold">Take Payment in Person</h5>
                      </CardHeader>
                      <CardBody className="card-inner" style={{ minHeight: '100px' }}>
                        <p>To take a payment in person click this link and hand the device over to the customer to complete.</p>
                      </CardBody>
                      <CardFooter>
                        {organisation.payment_methods && organisation.payment_methods.indexOf('MOTO_IN_PERSON') !== -1 && (
                          <Button
                            className="bg-violet-800 text-white float-right border-0"
                            onClick={(e) => openPaymentLink(e, MOTO_IN_PERSON)}
                          >
                            <WindowIcon className="h-4 w-4 mr-1" />
                            Open Payment Link
                          </Button>
                        )}
                        {organisation.payment_methods && organisation.payment_methods.indexOf('MOTO_IN_PERSON') === -1 && (
                          <span className="text-danger"><strong>This feature is disabled</strong></span>
                        )}
                      </CardFooter>
                    </Card>
                  </Col>

                  <Col md="6">
                    <Card className="card-bordered rounded-lg">
                      <CardHeader className="bg-slate-300 text-slate-800 py-2 px-4 relative">
                        <div className=" absolute top-0 right-0 text-violet-100 bg-slate-500 text-sm float-right -mt-[1px] px-4 py-[11.2px] rounded-xs font-bold">
                          MOTO
                        </div>
                        <h5 className="title font-extrabold">Take Payment over Phone</h5>
                      </CardHeader>
                      <CardBody className="card-inner" style={{ minHeight: '100px' }}>
                        <p>To take a payment over phone person click this link and request customer for card details.</p>

                      </CardBody>
                      <CardFooter>
                        {organisation.payment_methods && organisation.payment_methods.indexOf('MOTO_OTHER') !== -1 && (
                          <Button
                            className="bg-violet-800 text-white float-right border-0"
                            onClick={(e) => openPaymentLink(e, MOTO_OTHER)}
                          >
                            <PhoneIcon className="h-4 w-4 mr-1" />
                            Accept Payment Over Phone
                          </Button>
                        )}
                        {organisation.payment_methods && organisation.payment_methods.indexOf('MOTO_OTHER') === -1 && (
                          <span className="text-danger"><strong>This feature is disabled</strong></span>
                        )}
                      </CardFooter>
                    </Card>
                  </Col>

                </Row>
              </div>
            </div>
          </div>
        )}

        <Modal isOpen={archiveModelForm} toggle={toggleArchiveModelForm}>
          <ModalHeader
            toggle={toggleArchiveModelForm}
            className={`text-white bg-danger text-2xl`}
            close={
              <button className="close" onClick={toggleArchiveModelForm}>
                <Icon name="cross" className="text-white" />
              </button>
            }
          >
            Archive Payment Link
          </ModalHeader>
          <ModalBody>
            <p>Are you sure you would like to archive the payment link?</p>
          </ModalBody>
          <ModalFooter className="bg-light">
            <FormGroup>
              <Button className="bg-red-500" color="danger" type="submit" disabled={archiveModelFormProcessing} onClick={
                (ev) => {
                  ev.preventDefault();
                  archivePaymentLink();
                }
              } size="lg">
                {archiveModelFormProcessing ? <Spinner size="sm" color="light"> </Spinner> : 'Continue'}
              </Button>
            </FormGroup>
          </ModalFooter>
        </Modal>

      </Content>
    </React.Fragment>
  );
};
export default ProjectCardPage;
